import { Logger } from '@core/model/domain/logger';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  timeout,
  timer,
} from 'rxjs';

const CONVERT_SECONDS_TO_MS = 1000;
interface PollCmd<TApiArgs, TApiResponse> {
  intervalInSeconds: number;
  timeoutInSeconds: number;
  apiCall: (arg?: TApiArgs) => Observable<TApiResponse>;
  shouldContinuePolling: (arg: TApiResponse) => boolean;
  manualForceStop: BehaviorSubject<boolean>;
  logger?: Logger;
  pollingName: 'NEW_BANK_CONNECTION' | 'BANK_ACCOUNT_ANALYSIS_COMPLETION' | 'CASE_STATE_UPDATE';
}

export function poll<TApiArgs, TApiResponse>(
  input: PollCmd<TApiArgs, TApiResponse>
): Observable<TApiResponse> {
  input.logger?.logEvent(`Polling: started - ${input.pollingName}`);

  return input.apiCall().pipe(
    switchMap((apiResponse) => {
      if (input.shouldContinuePolling(apiResponse)) {
        return throwError(() => 'CONDITION_NOT_REACHED');
      }

      input.logger?.logEvent(`Polling: finished - ${input.pollingName}`);
      return of(apiResponse);
    }),
    retry({
      delay: () => {
        if (input.manualForceStop.value === true) {
          return throwError(() => 'STOPPED_POLLING_VIA_USER_ACTION');
        }

        return timer(input.intervalInSeconds * CONVERT_SECONDS_TO_MS);
      },
    }),
    timeout({ first: input.timeoutInSeconds * CONVERT_SECONDS_TO_MS }),
    catchError((err) => {
      /*
        Note: in case of STOPPED_POLLING_VIA_USER_ACTION the log
        may have a delay equal to the interval
      */
      if (err === 'STOPPED_POLLING_VIA_USER_ACTION') {
        input.logger?.logEvent(`Polling: stopped polling via user action - ${input.pollingName}`);
      } else if (err.name === 'TimeoutError') {
        input.logger?.logEvent(`Polling: timeout - ${input.pollingName}`);
      }

      throw err;
    })
  );
}
